import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Train, Clock, Search, Share2, MapPin, Navigation, Info, ArrowRight, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";

export default function Trains() {
  const [activeTab, setActiveTab] = useState("south"); // 'south' or 'north'
  const [searchQuery, setSearchQuery] = useState("");

  // 🔥 2026 Updated Train Data (Valid & Authentic) 🔥
  const southTrains = [
    { no: "16649", name: "Parasuram Express", route: "MAQ - NCJ", time: "06:41 AM" },
    { no: "16610", name: "MAQ Kozhikode Exp", route: "MAQ - CLT", time: "07:11 AM" },
    { no: "16345", name: "Netravati Express", route: "LTT - TVC", time: "07:42 AM" },
    { no: "16160", name: "Chennai Egmore Exp", route: "MAQ - MS", time: "08:35 AM" },
    { no: "16605", name: "Ernad Express", route: "MAQ - NCJ", time: "09:11 AM" },
    { no: "16324", name: "Mangaluru CBE Exp", route: "MAQ - CBE", time: "11:14 AM" },
    { no: "12602", name: "Mangaluru Chennai Mail", route: "MAQ - MAS", time: "03:35 PM" },
    { no: "16348", name: "Mangaluru TVC Exp", route: "MAQ - TVC", time: "04:25 PM" },
    { no: "16603", name: "Maveli Express", route: "MAQ - TVC", time: "07:15 PM" },
    { no: "16630", name: "Malabar Express", route: "MAQ - TVC", time: "08:25 PM" }
  ];

  const northTrains = [
    { no: "16604", name: "Maveli Express", route: "TVC - MAQ", time: "05:20 AM" },
    { no: "16629", name: "Malabar Express", route: "TVC - MAQ", time: "07:05 AM" },
    { no: "16347", name: "TVC Mangaluru Exp", route: "TVC - MAQ", time: "08:23 AM" },
    { no: "12601", name: "Chennai Mangaluru Mail", route: "MAS - MAQ", time: "09:35 AM" },
    { no: "22610", name: "Intercity SF Exp", route: "CBE - MAQ", time: "11:03 AM" },
    { no: "16606", name: "Ernad Express", route: "NCJ - MAQ", time: "02:37 PM" },
    { no: "16323", name: "Coimbatore MAQ Exp", route: "CBE - MAQ", time: "03:42 PM" },
    { no: "16159", name: "Chennai Mangaluru Exp", route: "MS - MAQ", time: "04:48 PM" },
    { no: "16650", name: "Parasuram Express", route: "NCJ - MAQ", time: "06:29 PM" },
    { no: "12617", name: "Mangala Lakshadweep", route: "ERS - NZM", time: "07:03 PM" }
  ];

  const currentData = activeTab === "south" ? southTrains : northTrains;

  const filteredTrains = currentData.filter(
    (train) =>
      train.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      train.no.includes(searchQuery)
  );

  const handleShareAll = async () => {
    const shareText = "INFO PAYANGADI: പഴയങ്ങാടി റെയിൽവേ സ്റ്റേഷനിലെ ട്രെയിൻ സമയവിവരങ്ങൾ അറിയാൻ ഈ ലിങ്ക് സന്ദർശിക്കുക.";
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ title: "Train Timings - Payangadi", text: shareText, url: shareUrl });
      } catch (error) {
        console.log("ഷെയർ ചെയ്യാൻ കഴിഞ്ഞില്ല:", error);
      }
    } else {
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success("Link copied!");
    }
  };

  return (
    <>
      <Helmet>
        <title>Train Timings - Payangadi | Info Payangadi</title>
        <meta name="description" content="Payangadi (PAZ) Railway Station updated train timings for 2026." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-4 sm:p-6 pb-28 selection:bg-blue-200">
        
        {/* 🔥 HERO SECTION 🔥 */}
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between mb-8 mt-2 gap-4">
          
          <div className="flex items-center gap-3">
            <div className="p-3 sm:p-4 bg-blue-100 text-blue-600 rounded-full shadow-inner">
              <Train className="w-8 h-8 sm:w-9 sm:h-9" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                Train <span className="text-blue-600">Timings</span>
              </h2>
              <div className="flex items-center gap-1 mt-0.5 text-gray-500">
                <MapPin size={14} />
                <p className="text-xs sm:text-sm font-medium">പഴയങ്ങാടി (PAZ)</p>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 w-full md:w-auto mt-2 md:mt-0">
            <button
              onClick={handleShareAll}
              className="flex-1 md:flex-none flex items-center justify-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-5 py-2.5 rounded-xl shadow-sm transition-all active:scale-95 font-bold text-sm"
            >
              <Share2 size={16} />
              <span>Share</span>
            </button>
          </div>

        </div>

        {/* 🔍 SEARCH + FILTER 🔥 */}
        <div className="max-w-3xl mx-auto mb-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              placeholder="ട്രെയിനിന്റെ പേരോ നമ്പറോ..."
              value={searchQuery}
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none transition-all text-sm sm:text-base font-medium bg-white"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Segmented Control for Direction */}
          <div className="flex bg-white p-1 rounded-2xl border border-gray-200 shadow-sm sm:w-auto w-full">
            <button
              onClick={() => setActiveTab("south")}
              className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                activeTab === "south" ? "bg-blue-50 text-blue-700" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              <ArrowLeft size={14} />കണ്ണൂർ 
            </button>
            <button
              onClick={() => setActiveTab("north")}
              className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                activeTab === "north" ? "bg-blue-50 text-blue-700" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              കാസർഗോഡ് <ArrowRight size={14} />
            </button>
          </div>
        </div>

        {/* 🔥 TRAINS LIST GRID 🔥 */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
          <AnimatePresence mode="popLayout">
            {filteredTrains.map((train, i) => (
              <motion.div
                key={train.no}
                layout
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: i * 0.03 }}
                whileHover={{ y: -2 }}
                className="bg-white p-4 sm:p-5 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group border border-gray-100"
              >
                {/* Background Decor */}
                <div className="absolute -top-8 -right-8 w-20 h-20 bg-blue-50 rounded-full group-hover:scale-150 transition-transform duration-500 z-0"></div>

                <div className="flex justify-between items-start relative z-10 mb-3 mt-1">
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                      {train.name}
                    </h3>
                    <div className="flex items-center gap-1 text-xs sm:text-sm font-medium text-gray-500 mt-1">
                      <Navigation size={12} className="text-gray-400" /> {train.route}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-1.5">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm sm:text-base font-black shadow-sm flex items-center gap-1.5">
                      <Clock size={14} /> {train.time}
                    </span>
                    <span className="text-[9px] uppercase tracking-wider font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded">
                      Daily
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-50 relative z-10 mt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm font-bold text-gray-800 tracking-wide">
                      Train No: {train.no}
                    </span>
                  </div>

                  <div className="p-1.5 bg-blue-50 text-blue-600 rounded-full">
                    <Train size={16} />
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {filteredTrains.length === 0 && (
            <div className="col-span-full text-center bg-white p-8 rounded-2xl border border-gray-100">
              <div className="bg-gray-50 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3">
                <Search size={28} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-700">No Trains Found</h3>
              <p className="text-sm text-gray-500 mt-1">നിങ്ങൾ തിരഞ്ഞ ട്രെയിൻ കണ്ടെത്താനായില്ല.</p>
            </div>
          )}
        </div>

        {/* 🌟 DISCLAIMER 🌟 */}
        <div className="max-w-5xl mx-auto mt-10">
          <div className="bg-amber-50 border border-amber-100 p-4 sm:p-5 rounded-2xl flex gap-3 items-start">
            <Info className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-sm font-bold text-amber-900">പ്രത്യേകം ശ്രദ്ധിക്കുക</h4>
              <p className="text-xs sm:text-sm text-amber-800 mt-1 font-medium leading-relaxed">
                ട്രെയിൻ സമയങ്ങളിൽ റെയിൽവേ ഇടയ്ക്കിടെ മാറ്റങ്ങൾ വരുത്താറുണ്ട്. യാത്ര പുറപ്പെടുന്നതിന് മുൻപ് റെയിൽവേയുടെ ഔദ്യോഗിക ആപ്പ് വഴിയോ സ്റ്റേഷനിൽ നിന്നോ വിവരങ്ങൾ ഉറപ്പുവരുത്തുക.
              </p>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}